import { gql } from '@apollo/client';
import Avatar from '@orn/shared-web/components/Avatar';
import { cx } from '@orn/shared-web/utils/cx';
import { getInitial } from '@orn/shared-web/utils/getInitials';
import * as React from 'react';

import { formatCalendarDate, toCurrency } from '../../../utils/formatting';
import EntityAvatar from '../../entity/EntityAvatar';

import type { TransactionSearchItemFragment } from './__generated__/TransactionSearchItem.gql';

interface ITransactionSearchItemProps {
  transaction: TransactionSearchItemFragment;
  onSelect?: () => void;
  isSelected?: boolean;
}

/**
 * A selectable transaction row in the global search results — merchant avatar
 * and name (falling back to the raw description), date, and signed amount.
 */
const TransactionSearchItem = ({ transaction, onSelect, isSelected = false }: ITransactionSearchItemProps) => {
  const handleClick = React.useCallback(() => {
    onSelect?.();
  }, [onSelect]);

  const label = transaction.merchant?.name ?? transaction.description;

  return (
    <div
      data-entity-search-item
      className={cx(
        'hover:bg-primary_hover h-13 flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2',
        isSelected && 'ring-brand ring-2 ring-inset'
      )}
      onClick={handleClick}
    >
      {transaction.merchant ? (
        <EntityAvatar entity={transaction.merchant} size="xs" />
      ) : (
        <Avatar contrastBorder size="xs" initials={getInitial(transaction.description)} />
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-primary truncate text-sm font-medium">{label}</span>
        <span className="text-tertiary truncate text-xs">{formatCalendarDate(transaction.date)}</span>
      </div>
      <span className="text-primary shrink-0 text-sm font-medium tabular-nums">
        {toCurrency(Number(transaction.amount))}
      </span>
    </div>
  );
};

const TransactionSearchItemFragmentGQL = gql`
  fragment TransactionSearchItem on Transaction {
    id
    amount
    date
    description
    merchant {
      id
      name
      ...EntityAvatar
    }
  }
  ${EntityAvatar.Fragment}
`;

export default Object.assign(TransactionSearchItem, { Fragment: TransactionSearchItemFragmentGQL });
